import { Container, Text, type ColorSource } from "pixi.js";
import type { Game } from "@engine/core/Game";
import type { UpdateSystem } from "@engine/core/Lifecycle";

export interface SpawnDamageOpts {
  x: number;
  y: number;
  amount: number;
  color?: ColorSource;
  /** Direction to float (degrees, 0 = up). */
  angleDeg?: number;
  durationS?: number;
  riseDistance?: number;
}

interface ActiveNumber {
  text: Text;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

const DEFAULT_DURATION_S = 0.9;
const DEFAULT_RISE = 28;

/**
 * Floating damage numbers. Spawn via `spawn()`; `update(dt)` floats each
 * number and fades alpha; finished numbers are destroyed.
 *
 *   const dmg = new DamageNumberSystem(game);
 *   game.addUpdateSystem(dmg);
 *   dmg.spawn({ x, y, amount: 42 });
 */
export class DamageNumberSystem implements UpdateSystem {
  private readonly root: Container;
  private readonly active: ActiveNumber[] = [];

  constructor(private readonly game: Game) {
    this.root = new Container({ label: "damage-numbers" });
    game.layers.attach("nameplate", this.root);
  }

  spawn(opts: SpawnDamageOpts): void {
    const angleRad = ((opts.angleDeg ?? 0) - 90) * (Math.PI / 180);
    const dist = opts.riseDistance ?? DEFAULT_RISE;
    const dur = opts.durationS ?? DEFAULT_DURATION_S;
    const text = new Text({
      text: String(Math.round(opts.amount)),
      style: {
        fontFamily: "ui-monospace, monospace",
        fontSize: 14,
        fill: opts.color ?? 0xfacc15,
        stroke: { color: 0x000000, width: 3 },
        fontWeight: "700",
      },
    });
    text.anchor.set(0.5, 0.5);
    text.position.set(opts.x, opts.y);
    this.root.addChild(text);
    this.active.push({
      text,
      vx: (Math.cos(angleRad) * dist) / dur,
      vy: (Math.sin(angleRad) * dist) / dur,
      life: 0,
      maxLife: dur,
    });
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const n = this.active[i];
      if (!n) continue;
      n.life += dt;
      const t = n.life / n.maxLife;
      if (t >= 1) {
        n.text.destroy();
        this.active.splice(i, 1);
        continue;
      }
      n.text.x += n.vx * dt;
      n.text.y += n.vy * dt;
      n.text.alpha = 1 - t * t;
    }
  }

  destroy(): void {
    for (const n of this.active) n.text.destroy();
    this.active.length = 0;
    if (this.root.parent) this.root.parent.removeChild(this.root);
    this.root.destroy({ children: true });
  }
}
